import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { api } from "../../api";
import { useNavigate } from "react-router-dom";
import { FiEdit, FiTrash } from "react-icons/fi";

export default function UsersTable() {
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [searchPhone, setSearchPhone] = useState("");
  const navigate = useNavigate();

  const isSearching = searchPhone.trim().length > 0;

  const fetchUsers = async (pageNumber = 1, phone = "") => {
    try {
      const res = await api.get("/users-list", {
        params: {
          page: pageNumber,
          pageSize,
          phone, // ✅ send phone to backend
        },
      });

      const usersArray = Array.isArray(res.data.users?.users)
        ? res.data.users.users
        : [];

      setUsers(usersArray);
      setTotalPages(res.data.users?.totalPages || 1);
      setPage(pageNumber);
    } catch {
      setUsers([]);
      setTotalPages(1);
    }
  };

  useEffect(() => {
    fetchUsers(page, searchPhone);
  }, [page, searchPhone]);

  const handleEdit = (user) => {
    navigate(`/users/${user.id}`, { state: { user } });
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;

    try {
      await api.delete(`/users/${userId}`);
      fetchUsers(page);
    } catch {
      alert("Failed to delete user.");
    }
  };

  return (
    <motion.div
      className="bg-black/40 p-6 rounded-2xl shadow-lg"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <h2 className="text-2xl font-semibold mb-4">Users</h2>

      <div className="mb-4 flex justify-end">
        <input
          type="text"
          placeholder="Search by phone number..."
          value={searchPhone}
          onChange={(e) => setSearchPhone(e.target.value)}
          className="w-full md:w-1/4 px-4 py-2 rounded bg-black/60 border border-gray-600 text-white"
        />
      </div>

      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-gray-600">
            <th className="p-2">Username</th>
            <th className="p-2">Phone</th>
            <th className="p-2">Balance</th>
            <th className="p-2">Action</th>
          </tr>
        </thead>
        <tbody>
          {users.length === 0 ? (
            <tr>
              <td colSpan={4} className="text-center py-4 text-gray-400">
                No users found.
              </td>
            </tr>
          ) : (
            users.map((u) => (
              <tr key={u.id} className="border-b border-gray-700">
                <td className="p-2">{u.username}</td>
                <td className="p-2">{u.phone}</td>
                <td className="p-2">{u.balance}</td>
                <td className="p-2 flex gap-3">
                  <button
                    onClick={() => handleEdit(u)}
                    className="text-blue-400 hover:text-blue-600"
                  >
                    <FiEdit />
                  </button>
                  <button
                    onClick={() => handleDelete(u.id)}
                    className="text-red-400 hover:text-red-600"
                  >
                    <FiTrash />
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* Pagination */}
      <div className="flex justify-between mt-4">
        <button
          disabled={page === 1}
          onClick={() => setPage((p) => p - 1)}
          className="bg-blue-700 px-4 py-2 rounded disabled:opacity-50"
        >
          Previous
        </button>

        <span className="text-gray-300 px-2 py-2">
          {searchPhone
            ? `Search results • Page ${page} of ${totalPages}`
            : `Page ${page} of ${totalPages}`}
        </span>

        <button
          disabled={page === totalPages}
          onClick={() => setPage((p) => p + 1)}
          className="bg-blue-700 px-4 py-2 rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </motion.div>
  );
}
